import React, { useState } from 'react'
import { Table, Form } from 'react-bootstrap-v5'
import { PlanningTabProps, downloadCsv, monthLabel, quantity, toInputMonth, usePlanningReport } from '../helpers/PlanningHelper'
import { PlanningNotice, ProductName } from './PlanningParts'

const percent = (value: number | null | undefined) => (value === null || value === undefined ? '' : `${value.toFixed(1)}%`)

/** The month `back` months before this one. */
const monthsAgo = (back: number) => {
	const today = new Date()
	return toInputMonth(new Date(today.getFullYear(), today.getMonth() - back, 1))
}

const PlanningForecastAccuracy: React.FC<PlanningTabProps> = ({ active, filters }) => {
	const [fromMonth, setFromMonth] = useState(monthsAgo(6))
	const [toMonth, setToMonth] = useState(monthsAgo(1))
	const [detailMonth, setDetailMonth] = useState('')

	const { loading, data, errors } = usePlanningReport(
		active,
		'forecast-accuracy',
		filters.idProjects && fromMonth && toMonth
			? { idProjects: filters.idProjects, method: filters.method, fromMonth, toMonth, detailMonth: detailMonth || null }
			: null
	)

	const months: any[] = data?.months ?? []
	const anySaved = months.some((month) => month.saved !== null)
	const detail: any[] | null = data?.detail ?? null
	const detailHasSaved = (detail ?? []).some((row) => row.savedQuantity !== null)

	const exportCsv = () => {
		downloadCsv(
			`forecast-vs-actual-${data.fromMonth}-to-${data.toMonth}.csv`,
			['Month', 'Delivered Volume', 'Calculated Forecast Volume', 'Error Whole Month %', 'Error By Blend %', 'Error By Product %', 'Saved Forecast Volume', 'Saved Error Whole Month %', 'Saved Error By Blend %', 'Saved Error By Product %'],
			months.map((month) => [
				monthLabel(month.month), month.actualVolume, month.system.volume, month.system.totalError, month.system.blendError, month.system.productError,
				month.saved?.volume, month.saved?.totalError, month.saved?.blendError, month.saved?.productError,
			]).concat([[
				'All months', data.overall.actualVolume, data.overall.system.volume, data.overall.system.totalError, data.overall.system.blendError, data.overall.system.productError,
				data.overall.saved?.volume, data.overall.saved?.totalError, data.overall.saved?.blendError, data.overall.saved?.productError,
			]])
		)
	}

	const exportDetailCsv = () => {
		downloadCsv(
			`forecast-vs-actual-${data.detailMonth}-products.csv`,
			['Product', 'Product Code', 'Blend', 'Pack Size', 'Unit', 'Calculated Forecast', 'Saved Forecast', 'Delivered', 'Difference', 'Difference Volume'],
			(detail ?? []).map((row) => [
				row.productName, row.productCode, row.itemName, row.packSize, row.unitName, row.systemQuantity, row.savedQuantity, row.actual,
				row.difference, row.difference * row.size,
			])
		)
	}

	return (
		<>
			<p className='text-muted small mb-3'>
				How close the forecast was to what was delivered. The calculated forecast is worked out again for each month from the months before it, so past months show even if nothing was saved.
				Error is the total difference as a percentage of the delivered volume: for the whole month, added up blend by blend, and product by product.
			</p>

			<div className='row align-items-end'>
				<div className='col-md-2'>
					<Form.Group className='mb-3' controlId='accuracyFrom'>
						<Form.Label>From Month</Form.Label>
						<Form.Control type='month' value={fromMonth} onChange={(event) => { setFromMonth(event.target.value); setDetailMonth('') }} />
					</Form.Group>
				</div>
				<div className='col-md-2'>
					<Form.Group className='mb-3' controlId='accuracyTo'>
						<Form.Label>To Month</Form.Label>
						<Form.Control type='month' value={toMonth} max={monthsAgo(1)} onChange={(event) => { setToMonth(event.target.value); setDetailMonth('') }} />
					</Form.Group>
				</div>
				<div className='col-md-8'>
					<div className='mb-3'>
						<button className='btn btn-sm btn-outline-primary p-3' onClick={exportCsv} disabled={months.length === 0}>
							<i className='fas fa-download me-1'></i>Excel (CSV)
						</button>
					</div>
				</div>
			</div>

			<PlanningNotice loading={loading} errors={errors} hasData={!!data} />

			{data && errors.length === 0 && (
				<>
					<Table responsive striped bordered hover size='sm' className='mb-4 bg-white'>
						<thead className='table-light'>
							<tr>
								<th rowSpan={2} className='align-middle'>Month</th>
								<th rowSpan={2} className='text-end align-middle'>Delivered Volume</th>
								<th colSpan={4} className='text-center'>Calculated forecast</th>
								{anySaved && <th colSpan={4} className='text-center'>Saved forecast</th>}
								<th rowSpan={2}></th>
							</tr>
							<tr>
								<th className='text-end'>Volume</th>
								<th className='text-end'>Whole Month</th>
								<th className='text-end'>By Blend</th>
								<th className='text-end'>By Product</th>
								{anySaved && (
									<>
										<th className='text-end'>Volume</th>
										<th className='text-end'>Whole Month</th>
										<th className='text-end'>By Blend</th>
										<th className='text-end'>By Product</th>
									</>
								)}
							</tr>
						</thead>
						<tbody>
							{months.map((month) => (
								<tr key={month.month} className={month.month === data.detailMonth ? 'table-active' : ''}>
									<td style={{ whiteSpace: 'nowrap' }}>{monthLabel(month.month)}</td>
									<td className='text-end'>{quantity(month.actualVolume)}</td>
									<td className='text-end'>{quantity(month.system.volume)}</td>
									<td className='text-end'>{percent(month.system.totalError)}</td>
									<td className='text-end'>{percent(month.system.blendError)}</td>
									<td className='text-end'>{percent(month.system.productError)}</td>
									{anySaved && (
										<>
											<td className='text-end'>{month.saved ? quantity(month.saved.volume) : ''}</td>
											<td className='text-end'>{percent(month.saved?.totalError)}</td>
											<td className='text-end'>{percent(month.saved?.blendError)}</td>
											<td className='text-end'>{percent(month.saved?.productError)}</td>
										</>
									)}
									<td className='text-center'>
										<button type='button' className='btn btn-sm btn-link p-0' onClick={() => setDetailMonth(month.month)}>
											Products
										</button>
									</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr>
								<th>All months</th>
								<th className='text-end'>{quantity(data.overall.actualVolume)}</th>
								<th className='text-end'>{quantity(data.overall.system.volume)}</th>
								<th className='text-end'>{percent(data.overall.system.totalError)}</th>
								<th className='text-end'>{percent(data.overall.system.blendError)}</th>
								<th className='text-end'>{percent(data.overall.system.productError)}</th>
								{anySaved && (
									<>
										<th className='text-end'>{data.overall.saved ? quantity(data.overall.saved.volume) : ''}</th>
										<th className='text-end'>{percent(data.overall.saved?.totalError)}</th>
										<th className='text-end'>{percent(data.overall.saved?.blendError)}</th>
										<th className='text-end'>{percent(data.overall.saved?.productError)}</th>
									</>
								)}
								<th></th>
							</tr>
						</tfoot>
					</Table>

					{detail && (
						<>
							<div className='d-flex align-items-center mb-2'>
								<h6 className='mb-0 me-3'>{monthLabel(data.detailMonth)}: forecast and delivery by product</h6>
								<button className='btn btn-sm btn-outline-primary py-1 px-3' onClick={exportDetailCsv} disabled={detail.length === 0}>
									<i className='fas fa-download me-1'></i>Excel (CSV)
								</button>
							</div>
							<Table responsive striped bordered hover size='sm' className='mb-0 bg-white'>
								<thead className='table-light'>
									<tr>
										<th>SL</th>
										<th>Product</th>
										<th>Pack Size</th>
										<th>Unit</th>
										<th className='text-end'>Calculated Forecast</th>
										{detailHasSaved && <th className='text-end'>Saved Forecast</th>}
										<th className='text-end'>Delivered</th>
										<th className='text-end' title='Forecast less delivered; the saved forecast where the month has one'>Difference</th>
										<th className='text-end'>Difference Volume</th>
									</tr>
								</thead>
								<tbody>
									{detail.length === 0 && (
										<tr>
											<td colSpan={9} className='text-center text-muted py-4'>Nothing was forecast or delivered in this month.</td>
										</tr>
									)}
									{detail.map((row, index) => (
										<tr key={row.idProducts}>
											<td>{index + 1}</td>
											<td><ProductName row={row} /></td>
											<td>{row.packSize}</td>
											<td>{row.unitName}</td>
											<td className='text-end'>{quantity(row.systemQuantity, 2)}</td>
											{detailHasSaved && <td className='text-end'>{quantity(row.savedQuantity, 2)}</td>}
											<td className='text-end'>{quantity(row.actual, 2)}</td>
											<td className={`text-end ${row.difference < 0 ? 'text-danger' : ''}`}>{quantity(row.difference, 2)}</td>
											<td className={`text-end ${row.difference < 0 ? 'text-danger' : ''}`}>{quantity(row.difference * row.size)} {row.itemUnitName}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</>
					)}
				</>
			)}
		</>
	)
}

export default PlanningForecastAccuracy
